/**
 * PricingModal Component
 *
 * Modal for setting custom pricing.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDates: Date[];
  currentPrice: number;
  onSetPrice: (price: number) => Promise<void>;
  onClearPrice: () => Promise<void>;
  isLoading: boolean;
}

const formatDateRange = (dates: Date[]): string => {
  if (dates.length === 0) return '';
  if (dates.length === 1) {
    return dates[0].toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
  const first = sortedDates[0];
  const last = sortedDates[sortedDates.length - 1];

  return `${first.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })} - ${last.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
};

export const PricingModal: React.FC<PricingModalProps> = ({
  isOpen,
  onClose,
  selectedDates,
  currentPrice,
  onSetPrice,
  onClearPrice,
  isLoading,
}) => {
  const [price, setPrice] = useState(currentPrice.toString());

  if (!isOpen) return null;

  const handleSetPrice = async () => {
    const numPrice = parseFloat(price);
    if (isNaN(numPrice) || numPrice <= 0) {
      toast.error('Please enter a valid price');
      return;
    }
    await onSetPrice(numPrice);
    onClose();
  };

  const handleClearPrice = async () => {
    await onClearPrice();
    onClose();
  };

  const priceNum = parseFloat(price) || 0;
  const difference = priceNum - currentPrice;
  const percentChange = currentPrice > 0 ? ((difference / currentPrice) * 100).toFixed(0) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Set Custom Price</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">Selected dates:</p>
          <p className="font-medium text-gray-900">
            {formatDateRange(selectedDates)}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {selectedDates.length} {selectedDates.length === 1 ? 'night' : 'nights'}
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price per night
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
              $
            </span>
            <input
              type="number"
              min="1"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full pl-8 pr-16 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
              placeholder="0"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">
              USD
            </span>
          </div>

          {/* Price comparison */}
          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Base price</span>
              <span className="text-gray-900">${currentPrice}</span>
            </div>
            {priceNum > 0 && priceNum !== currentPrice && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-gray-600">Difference</span>
                <span
                  className={`font-medium ${
                    difference > 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {difference > 0 ? '+' : ''}${difference.toFixed(0)} ({percentChange}%)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Quick adjustment buttons */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">Quick adjust:</p>
          <div className="flex gap-2">
            {[-20, -10, 10, 20].map((percent) => (
              <button
                key={percent}
                type="button"
                onClick={() => {
                  const newPrice = Math.round(currentPrice * (1 + percent / 100));
                  setPrice(newPrice.toString());
                }}
                className={`flex-1 py-2 text-sm font-medium rounded-lg border ${
                  percent < 0
                    ? 'border-red-200 text-red-600 hover:bg-red-50'
                    : 'border-green-200 text-green-600 hover:bg-green-50'
                }`}
              >
                {percent > 0 ? '+' : ''}{percent}%
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleSetPrice}
            disabled={isLoading || !price}
            className="flex-1 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Set price'}
          </button>
          <button
            onClick={handleClearPrice}
            disabled={isLoading}
            className="px-6 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-4 text-center">
          Custom prices override your base and weekend rates.
        </p>
      </div>
    </div>
  );
};

// Icon
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default PricingModal;
